import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ALLOX_ROLE_KEY, Contents, AccessPermission } from '../constants/allox-constant';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  // 作成権限
  public createPermission: object;
  // 変更権限
  public updatePermission: object;
  // 参照権限
  public readPermission: object;
  // 削除権限
  public deletePermission: object;
  // 実行権限
  public executePermission: object;
  // 初期表示画面
  public initialContents: string;

  constructor() { }

  // 権限セット
  public setPermission() {

    // Role取得
    const helper = new JwtHelperService();
    const alloxRole = helper.decodeToken(localStorage.getItem(ALLOX_ROLE_KEY));
    if (alloxRole != null) {
      const permissionGroups = alloxRole.permission_groups;

      // 初期表示画面セット
      this.initialContents = alloxRole.initial_contents;

      // 権限セット
      this.setCreatePermission(permissionGroups);
      this.setUpdatePermission(permissionGroups);
      this.setReadPermission(permissionGroups);
      this.setDeletePermission(permissionGroups);
      this.setExecutePermission(permissionGroups);
    }
  }

  // 作成権限取得
  public hasCreatePermission(contents: Contents) {
    if (this.createPermission === undefined) {
      this.setPermission();
    }
    return this.createPermission[contents];
  }

  // 変更権限取得
  public hasUpdatePermission(contents: Contents) {
    if (this.updatePermission === undefined) {
      this.setPermission();
    }
    return this.updatePermission[contents];
  }

  // 参照権限取得
  public hasReadPermission(contents: Contents) {
    if (this.readPermission === undefined) {
      this.setPermission();
    }
    return this.readPermission[contents];
  }

  // 削除権限取得
  public hasDeletePermission(contents: Contents) {
    if (this.deletePermission === undefined) {
      this.setPermission();
    }
    return this.deletePermission[contents];
  }

  // 実行権限取得
  public hasExecutePermission(contents: Contents) {
    if (this.executePermission === undefined) {
      this.setPermission();
    }
    return this.executePermission[contents];
  }

  // 初期表示画面取得
  public hasInitialContents() {
    if (this.initialContents === undefined) {
      this.setPermission();
    }
    return this.initialContents;
  }

  // 作成権限セット
  private setCreatePermission(permissionGroups: any) {
    this.createPermission = {
      [Contents.DELIVERY_SET]: this.checkCreatePermission(permissionGroups, Contents.DELIVERY_SET),
      [Contents.DESIGN_TEMPLATE]: this.checkCreatePermission(permissionGroups, Contents.DESIGN_TEMPLATE),
      [Contents.STANDARD_DESIGN_TEMPLATE]: this.checkCreatePermission(permissionGroups, Contents.STANDARD_DESIGN_TEMPLATE),
      [Contents.PLACEMENT_RELATION]: this.checkCreatePermission(permissionGroups, Contents.PLACEMENT_RELATION),
      [Contents.AD_REQUEST]: this.checkCreatePermission(permissionGroups, Contents.AD_REQUEST),
      [Contents.PLACEMENT]: this.checkCreatePermission(permissionGroups, Contents.PLACEMENT),
      [Contents.TESTER_DEVICE]: this.checkCreatePermission(permissionGroups, Contents.TESTER_DEVICE),
      [Contents.DEVICE_MODEL]: this.checkCreatePermission(permissionGroups, Contents.DEVICE_MODEL),
      [Contents.DEVICE_MODEL_GROUP]: this.checkCreatePermission(permissionGroups, Contents.DEVICE_MODEL_GROUP),
      [Contents.OPERATING_SYSTEM]: this.checkCreatePermission(permissionGroups, Contents.OPERATING_SYSTEM),
      [Contents.OS_GROUP]: this.checkCreatePermission(permissionGroups, Contents.OS_GROUP),
      [Contents.PLATFORM_OS_GROUP]: this.checkCreatePermission(permissionGroups, Contents.PLATFORM_OS_GROUP),
      [Contents.SDK_VERSION]: this.checkCreatePermission(permissionGroups, Contents.SDK_VERSION),
      [Contents.SDK_VERSION_GROUP]: this.checkCreatePermission(permissionGroups, Contents.SDK_VERSION_GROUP),
      [Contents.PLATFORM_SDK_VERSION_GROUP]: this.checkCreatePermission(permissionGroups, Contents.PLATFORM_SDK_VERSION_GROUP),
      [Contents.DEAL]: this.checkCreatePermission(permissionGroups, Contents.DEAL),
      [Contents.TOPICS_URL]: this.checkCreatePermission(permissionGroups, Contents.TOPICS_URL),
      [Contents.MULTI_AD_CONFIGS]: this.checkCreatePermission(permissionGroups, Contents.MULTI_AD_CONFIGS),
      [Contents.AD_REQUEST_PARAMETER_TEMPLATE]: this.checkCreatePermission(permissionGroups, Contents.AD_REQUEST_PARAMETER_TEMPLATE),
      [Contents.USERS]: this.checkCreatePermission(permissionGroups, Contents.USERS),
      [Contents.PLATFORM_RESERVATION]: this.checkCreatePermission(permissionGroups, Contents.PLATFORM_RESERVATION),
    };
  }

  // 変更権限セット
  private setUpdatePermission(permissionGroups: any) {
    this.updatePermission = {
      [Contents.DELIVERY_SET]: this.checkUpdatePermission(permissionGroups, Contents.DELIVERY_SET),
      [Contents.DESIGN_TEMPLATE]: this.checkUpdatePermission(permissionGroups, Contents.DESIGN_TEMPLATE),
      [Contents.STANDARD_DESIGN_TEMPLATE]: this.checkUpdatePermission(permissionGroups, Contents.STANDARD_DESIGN_TEMPLATE),
      [Contents.PLACEMENT_RELATION]: this.checkUpdatePermission(permissionGroups, Contents.PLACEMENT_RELATION),
      [Contents.AD_REQUEST]: this.checkUpdatePermission(permissionGroups, Contents.AD_REQUEST),
      [Contents.PLACEMENT]: this.checkUpdatePermission(permissionGroups, Contents.PLACEMENT),
      [Contents.TESTER_DEVICE]: this.checkUpdatePermission(permissionGroups, Contents.TESTER_DEVICE),
      [Contents.DEVICE_MODEL]: this.checkUpdatePermission(permissionGroups, Contents.DEVICE_MODEL),
      [Contents.DEVICE_MODEL_GROUP]: this.checkUpdatePermission(permissionGroups, Contents.DEVICE_MODEL_GROUP),
      [Contents.OPERATING_SYSTEM]: this.checkUpdatePermission(permissionGroups, Contents.OPERATING_SYSTEM),
      [Contents.OS_GROUP]: this.checkUpdatePermission(permissionGroups, Contents.OS_GROUP),
      [Contents.PLATFORM_OS_GROUP]: this.checkUpdatePermission(permissionGroups, Contents.PLATFORM_OS_GROUP),
      [Contents.SDK_VERSION]: this.checkUpdatePermission(permissionGroups, Contents.SDK_VERSION),
      [Contents.SDK_VERSION_GROUP]: this.checkUpdatePermission(permissionGroups, Contents.SDK_VERSION_GROUP),
      [Contents.PLATFORM_SDK_VERSION_GROUP]: this.checkUpdatePermission(permissionGroups, Contents.PLATFORM_SDK_VERSION_GROUP),
      [Contents.DEAL]: this.checkUpdatePermission(permissionGroups, Contents.DEAL),
      [Contents.TOPICS_URL]: this.checkUpdatePermission(permissionGroups, Contents.TOPICS_URL),
      [Contents.MULTI_AD_CONFIGS]: this.checkUpdatePermission(permissionGroups, Contents.MULTI_AD_CONFIGS),
      [Contents.AD_REQUEST_PARAMETER_TEMPLATE]: this.checkUpdatePermission(permissionGroups, Contents.AD_REQUEST_PARAMETER_TEMPLATE),
      [Contents.USERS]: this.checkUpdatePermission(permissionGroups, Contents.USERS),
      [Contents.PLATFORM_RESERVATION]: this.checkUpdatePermission(permissionGroups, Contents.PLATFORM_RESERVATION),
      [Contents.MAINTENANCE]: this.checkUpdatePermission(permissionGroups, Contents.MAINTENANCE),
    };
  }

  // 参照権限セット
  private setReadPermission(permissionGroups: any) {
    this.readPermission = {
      [Contents.DELIVERY_SET]: this.checkReadPermission(permissionGroups, Contents.DELIVERY_SET),
      [Contents.DESIGN_TEMPLATE]: this.checkReadPermission(permissionGroups, Contents.DESIGN_TEMPLATE),
      [Contents.STANDARD_DESIGN_TEMPLATE]: this.checkReadPermission(permissionGroups, Contents.STANDARD_DESIGN_TEMPLATE),
      [Contents.PLACEMENT_RELATION]: this.checkReadPermission(permissionGroups, Contents.PLACEMENT_RELATION),
      [Contents.AD_REQUEST]: this.checkReadPermission(permissionGroups, Contents.AD_REQUEST),
      [Contents.PLACEMENT]: this.checkReadPermission(permissionGroups, Contents.PLACEMENT),
      [Contents.TESTER_DEVICE]: this.checkReadPermission(permissionGroups, Contents.TESTER_DEVICE),
      [Contents.DEVICE_MODEL]: this.checkReadPermission(permissionGroups, Contents.DEVICE_MODEL),
      [Contents.DEVICE_MODEL_GROUP]: this.checkReadPermission(permissionGroups, Contents.DEVICE_MODEL_GROUP),
      [Contents.OPERATING_SYSTEM]: this.checkReadPermission(permissionGroups, Contents.OPERATING_SYSTEM),
      [Contents.OS_GROUP]: this.checkReadPermission(permissionGroups, Contents.OS_GROUP),
      [Contents.PLATFORM_OS_GROUP]: this.checkReadPermission(permissionGroups, Contents.PLATFORM_OS_GROUP),
      [Contents.SDK_VERSION]: this.checkReadPermission(permissionGroups, Contents.SDK_VERSION),
      [Contents.SDK_VERSION_GROUP]: this.checkReadPermission(permissionGroups, Contents.SDK_VERSION_GROUP),
      [Contents.PLATFORM_SDK_VERSION_GROUP]: this.checkReadPermission(permissionGroups, Contents.PLATFORM_SDK_VERSION_GROUP),
      [Contents.DEAL]: this.checkReadPermission(permissionGroups, Contents.DEAL),
      [Contents.EXCHANGE_RATE]: this.checkReadPermission(permissionGroups, Contents.EXCHANGE_RATE),
      [Contents.EMERGENCY_STOP]: this.checkReadPermission(permissionGroups, Contents.EMERGENCY_STOP),
      [Contents.TOPICS_URL]: this.checkReadPermission(permissionGroups, Contents.TOPICS_URL),
      [Contents.MULTI_AD_CONFIGS]: this.checkReadPermission(permissionGroups, Contents.MULTI_AD_CONFIGS),
      [Contents.AD_REQUEST_PARAMETER_TEMPLATE]: this.checkReadPermission(permissionGroups, Contents.AD_REQUEST_PARAMETER_TEMPLATE),
      [Contents.USERS]: this.checkReadPermission(permissionGroups, Contents.USERS),
      [Contents.PLATFORM_RESERVATION]: this.checkReadPermission(permissionGroups, Contents.PLATFORM_RESERVATION),
      [Contents.MAINTENANCE]: this.checkReadPermission(permissionGroups, Contents.MAINTENANCE),
    };
  }

  // 削除権限セット
  private setDeletePermission(permissionGroups: any) {
    this.deletePermission = {
      [Contents.DELIVERY_SET]: this.checkDeletePermission(permissionGroups, Contents.DELIVERY_SET),
      [Contents.DESIGN_TEMPLATE]: this.checkDeletePermission(permissionGroups, Contents.DESIGN_TEMPLATE),
      [Contents.STANDARD_DESIGN_TEMPLATE]: this.checkDeletePermission(permissionGroups, Contents.STANDARD_DESIGN_TEMPLATE),
      [Contents.PLACEMENT_RELATION]: this.checkDeletePermission(permissionGroups, Contents.PLACEMENT_RELATION),
      [Contents.AD_REQUEST]: this.checkDeletePermission(permissionGroups, Contents.AD_REQUEST),
      [Contents.TESTER_DEVICE]: this.checkDeletePermission(permissionGroups, Contents.TESTER_DEVICE),
      [Contents.DEVICE_MODEL]: this.checkDeletePermission(permissionGroups, Contents.DEVICE_MODEL),
      [Contents.DEVICE_MODEL_GROUP]: this.checkDeletePermission(permissionGroups, Contents.DEVICE_MODEL_GROUP),
      [Contents.OPERATING_SYSTEM]: this.checkDeletePermission(permissionGroups, Contents.OPERATING_SYSTEM),
      [Contents.OS_GROUP]: this.checkDeletePermission(permissionGroups, Contents.OS_GROUP),
      [Contents.SDK_VERSION]: this.checkDeletePermission(permissionGroups, Contents.SDK_VERSION),
      [Contents.SDK_VERSION_GROUP]: this.checkDeletePermission(permissionGroups, Contents.SDK_VERSION_GROUP),
      [Contents.DEAL]: this.checkDeletePermission(permissionGroups, Contents.DEAL),
      [Contents.AD_REQUEST_PARAMETER_TEMPLATE]: this.checkDeletePermission(permissionGroups, Contents.AD_REQUEST_PARAMETER_TEMPLATE),
      [Contents.USERS]: this.checkDeletePermission(permissionGroups, Contents.USERS),
      [Contents.PLATFORM_RESERVATION]: this.checkDeletePermission(permissionGroups, Contents.PLATFORM_RESERVATION),
    };
  }

  // 実行権限セット
  private setExecutePermission(permissionGroups: any) {
    this.executePermission = {
      [Contents.EMERGENCY_STOP]: this.checkExecutePermission(permissionGroups, Contents.EMERGENCY_STOP),
      [Contents.PLATFORMS_KEYS]: this.checkExecutePermission(permissionGroups, Contents.PLATFORMS_KEYS),
      [Contents.EXCHANGE_RATE]: this.checkExecutePermission(permissionGroups, Contents.EXCHANGE_RATE),
    };
  }

  // 作成権限チェック
  private checkCreatePermission(permissionGroups: any, contents: string) {
    return permissionGroups.some(v => (v.contents === contents && v.access_permission === AccessPermission.CREATE));
  }

  // 変更権限チェック
  private checkUpdatePermission(permissionGroups: any, contents: string) {
    return permissionGroups.some(v => (v.contents === contents && v.access_permission === AccessPermission.UPDATE));
  }

  // 参照権限チェック
  private checkReadPermission(permissionGroups: any, contents: string) {
    return permissionGroups.some(v => (v.contents === contents && v.access_permission === AccessPermission.READ));
  }

  // 削除権限チェック
  private checkDeletePermission(permissionGroups: any, contents: string) {
    return permissionGroups.some(v => (v.contents === contents && v.access_permission === AccessPermission.DELETE));
  }

  // 実行権限チェック
  private checkExecutePermission(permissionGroups: any, contents: string) {
    return permissionGroups.some(v => (v.contents === contents && v.access_permission === AccessPermission.EXECUTE));
  }
}
